import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { HelmetProvider } from 'react-helmet-async';
import { CssBaseline } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { ThemeContextProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard/Dashboard';
import Collection from './pages/Collection/Collection';
import CollectionCardDetail from './pages/Collection/CollectionCardDetail';
import SoldInventory from './pages/SoldInventory/SoldInventory';
import Sets from './pages/Sets/Sets';
import MasterSets from './pages/Sets/MasterSets';
import SetDetails from './pages/SetDetails/SetDetails';
import Grading from './pages/Grading/Grading';
import Analytics from './pages/Analytics/Analytics';
import Settings from './pages/Settings/Settings';
import TcgDrops from './pages/InventoryTracker/TcgDrops';
import TradeCalculator from './pages/Trades/TradeCalculator';
import TradeHistory from './pages/Trades/TradeHistory';
import OnePieceSets from './pages/OnePieceSets/OnePieceSets';
import OnePieceGlobalSearch from './pages/OnePieceSearch/OnePieceGlobalSearch';
import OnePieceSetDetails from './pages/OnePieceSetDetails/OnePieceSetDetails';
import SearchPage from './pages/UnifiedSearch/UnifiedSearch';
import OnePieceCardDetails from './pages/OnePieceCardDetails/OnePieceCardDetails';
import PokemonCardDetails from './pages/PokemonCardDetails/PokemonCardDetails';
import OnePieceDeckBuilder from './pages/OnePieceDeckBuilder/OnePieceDeckBuilder';
import SealedProductTracker from './pages/Sealed/SealedProductTracker';
import PublicCollectionView from './pages/PublicCollectionView';
import NfcSharePage from './pages/NfcShare/NfcSharePage';
import LegalPage from './pages/LegalPage';
import BuyList from './pages/BuyList/BuyList';
import Storefront from './pages/Storefront/Storefront';
import StoreAdmin from './pages/StoreAdmin/StoreAdmin';
import EventsPage from './pages/Events/EventsPage';
import TournamentsPage from './pages/Tournaments/TournamentsPage';
import ContactPage from './pages/Contact';
import { PagePersistenceProvider } from './contexts/PagePersistenceContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { createPersistedQueryClient } from './utils/queryClientPersister';
import './utils/debugAuth'; // Load debug utility

// Log environment configuration for debugging
console.log('Environment Configuration:', {
  REACT_APP_USER_POOL_ID: process.env.REACT_APP_USER_POOL_ID,
  REACT_APP_CLIENT_ID: process.env.REACT_APP_CLIENT_ID,
  REACT_APP_USER_POOL_CLIENT_ID: process.env.REACT_APP_USER_POOL_CLIENT_ID,
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  REACT_APP_STAGE: process.env.REACT_APP_STAGE,
  REACT_APP_POKEMONTCG_KEY: process.env.REACT_APP_POKEMONTCG_KEY ? 'Present' : 'Missing',
  REACT_APP_JUSTTCG_API_KEY: process.env.REACT_APP_JUSTTCG_API_KEY ? 'Present' : 'Missing',
});

// Create a persisted client for React Query (survives page refreshes)
const queryClient = createPersistedQueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeContextProvider>
        <NotificationProvider>
          <CssBaseline />
          <HelmetProvider>
            <AuthProvider>
              <Router>
                <PagePersistenceProvider>
                <Routes>
                  {/* Landing page without layout */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/privacy" element={<LegalPage type="privacy" />} />
                <Route path="/tos" element={<LegalPage type="tos" />} />
  
                {/* Public collection view (no auth required) */}
                <Route
                  path="/collection/:userId"
                  element={<PublicCollectionView />}
                />
  
                {/* Store NFC Digital Hub routes */}
                <Route path="/store-card" element={<NfcSharePage />} />
                <Route path="/store-card/:storeId" element={<NfcSharePage />} />
  
                {/* App routes with layout */}
                <Route element={<Layout />}>
                  <Route path="/dashboard" element={<React.Fragment />} />
                  <Route path="/collection" element={<React.Fragment />} />
                  <Route path="/packs" element={<React.Fragment />} />
                  <Route
                    path="/collection/:setId/:cardId"
                    element={<CollectionCardDetail />}
                  />
                  <Route path="/sold" element={<React.Fragment />} />
                  <Route path="/buy-list" element={<BuyList />} />
                  <Route path="/store" element={<Storefront />} />
                  <Route path="/events" element={<EventsPage />} />
                  <Route path="/tournaments" element={<TournamentsPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/admin/store" element={<StoreAdmin />} />
  
                  <Route path="/master-sets" element={<React.Fragment />} />
                  <Route path="/sets" element={<React.Fragment />} />
                  <Route path="/sets/:setId" element={<React.Fragment />} />
                  <Route
                    path="/sets/:setId/cards/:cardId"
                    element={<PokemonCardDetails />}
                  />
                  <Route path="/grading" element={<React.Fragment />} />
                  <Route path="/analytics" element={<Analytics />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/search" element={<React.Fragment />} />
                  <Route
                    path="/search/:setId/:cardId"
                    element={<CollectionCardDetail />}
                  />
                  <Route path="/drops" element={<React.Fragment />} />
                  <Route path="/trades/new" element={<TradeCalculator />} />
                  <Route path="/trades/history" element={<React.Fragment />} />
  
                  {/* One Piece TCG routes */}
                  <Route path="/onepiece/sets" element={<React.Fragment />} />
                  <Route
                    path="/onepiece/sets/:setId"
                    element={<React.Fragment />}
                  />
                  <Route
                    path="/onepiece/sets/:setId/cards/:cardId"
                    element={<OnePieceCardDetails />}
                  />
                  <Route
                    path="/onepiece/search"
                    element={<OnePieceGlobalSearch />}
                  />
                  <Route
                    path="/onepiece/sealed"
                    element={<SealedProductTracker />}
                  />
                  <Route
                    path="/onepiece/deck-builder"
                    element={<React.Fragment />}
                  />
                </Route>
                  </Routes>
                </PagePersistenceProvider>
              </Router>
            </AuthProvider>
          </HelmetProvider>
        </NotificationProvider>
      </ThemeContextProvider>
    </QueryClientProvider>
  );
}

export default App;
